<template>
  <div class="n8n-page">
    <!-- Header -->
    <header class="n8n-header">
      <div class="n8n-header__brand">
        <img src="../assets/images/logo2.png" class="n8n-header__logo" loading="lazy" alt="Logo" />
        <div class="n8n-header__info">
          <span class="n8n-header__title">UFA Easy888</span>
          <span class="n8n-header__badge">{{ code }}</span>
        </div>
      </div>
      <div class="n8n-header__avatar" :style="{ backgroundImage: `url(${pictureUrl})` }"></div>
    </header>

    <!-- Main Content -->
    <main class="n8n-main">
      <!-- Page Hero -->
      <div class="n8n-hero">
        <div class="n8n-hero__icon n8n-hero__icon--withdraw">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M4 12h16M12 4l8 8-8 8" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <h1 class="n8n-hero__title">แจ้งถอนเงิน</h1>
        <p class="n8n-hero__subtitle">ถอนเงินเข้าบัญชีของคุณ</p>
      </div>

      <!-- Balance Card -->
      <div class="n8n-balance-card">
        <div class="n8n-balance-card__label">ยอดเครดิตคงเหลือ</div>
        <div class="n8n-balance-card__amount">
          <span class="n8n-balance-card__value">{{ creditTotal }}</span>
          <span class="n8n-balance-card__currency">บาท</span>
        </div>
      </div>

      <!-- Form Card -->
      <div class="n8n-card">
        <div class="n8n-card__header">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
            <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
          </svg>
          <span>ข้อมูลการถอนเงิน</span>
        </div>
        
        <div class="n8n-card__body">
          <!-- Name Field -->
          <div class="n8n-field">
            <label class="n8n-field__label">ชื่อเจ้าของบัญชี</label>
            <div class="n8n-input n8n-input--disabled">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/>
                <circle cx="12" cy="7" r="4"/>
              </svg>
              <span>{{ name }}</span>
            </div>
          </div>

          <!-- Amount Field -->
          <div class="n8n-field">
            <label class="n8n-field__label" for="withdraw-amount">จำนวนเงินที่ต้องการถอน</label>
            <div class="n8n-input" :class="{ 'n8n-input--error': error }">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="12" y1="1" x2="12" y2="23"/>
                <path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/>
              </svg>
              <input 
                id="withdraw-amount"
                v-model="money" 
                ref="money"
                type="number"
                min="50"
                :max="maxWithdrawAmount"
                aria-describedby="withdraw-amount-help withdraw-amount-error"
                :aria-invalid="error ? 'true' : 'false'"
                placeholder="ขั้นต่ำ 50 บาท"
                @keypress="validateNumber"
                @input="clearValidationError"
              />
            </div>
            <span id="withdraw-amount-help" class="n8n-field__helper">ขั้นต่ำ 50 บาท</span>
            <span v-if="error" id="withdraw-amount-error" class="n8n-field__error" role="alert">
              {{ validationError }}
            </span>
          </div>

          <!-- Quick Amounts -->
          <div class="n8n-quick-amounts">
            <span class="n8n-quick-amounts__label">เลือกจำนวนด่วน</span>
            <div class="n8n-quick-amounts__grid">
              <button 
                class="n8n-quick-btn" 
                @click="setAmount(100)"
                :disabled="100 > maxWithdrawAmount"
              >100 ฿</button>
              <button 
                class="n8n-quick-btn" 
                @click="setAmount(500)"
                :disabled="500 > maxWithdrawAmount"
              >500 ฿</button>
              <button 
                class="n8n-quick-btn" 
                @click="setAmount(1000)"
                :disabled="1000 > maxWithdrawAmount"
              >1,000 ฿</button>
              <button 
                class="n8n-quick-btn" 
                @click="setAmount(5000)"
                :disabled="5000 > maxWithdrawAmount"
              >5,000 ฿</button>
            </div>
          </div>
        </div>
      </div>

      <TurnoverStatusCard
        v-if="submitError"
        :details="turnoverDetails"
        :message="submitError"
      />

      <div v-if="isSubmitted" class="n8n-success-banner" role="status" aria-live="polite">
        <span class="n8n-success-banner__icon" aria-hidden="true">✓</span>
        <div>
          <strong>แจ้งถอนเงินเรียบร้อยแล้ว</strong>
          <span>ระบบส่งรายการให้แอดมินตรวจสอบแล้ว</span>
        </div>
      </div>

      <!-- Bank Account Card -->
      <div class="n8n-card">
        <div class="n8n-card__header">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="1" y="4" width="22" height="16" rx="2"/>
            <line x1="1" y1="10" x2="23" y2="10"/>
          </svg>
          <span>บัญชีปลายทาง</span>
          <span class="n8n-card__badge">เลือกแล้ว</span>
        </div>
        
        <div class="n8n-card__body">
          <div class="n8n-bank-info">
            <div class="n8n-bank-info__icon">
              <img
                v-if="bankImage"
                :src="bankImage"
                alt="Bank"
              />
            </div>
            <div class="n8n-bank-info__details">
              <div class="n8n-bank-info__bank">{{ bank }}</div>
              <div class="n8n-bank-info__number">{{ accountno }}</div>
              <div class="n8n-bank-info__name">{{ name }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Submit Button -->
      <div class="n8n-submit" v-if="isShow">
        <button 
          class="n8n-btn n8n-btn--primary n8n-btn--full"
          @click="confirm" 
          :disabled="isDisabled || isSubmitted"
        >
          <svg v-if="!isDisabled && !isSubmitted" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="22" y1="2" x2="11" y2="13"/>
            <polygon points="22 2 15 22 11 13 2 9 22 2"/>
          </svg>
          <span v-if="isDisabled" class="n8n-spinner"></span>
          {{ isDisabled ? 'กำลังตรวจสอบ...' : (isSubmitted ? 'แจ้งถอนแล้ว' : 'ตรวจสอบและแจ้งถอน') }}
        </button>
      </div>

      <!-- Info Footer -->
      <div class="n8n-info-footer">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
        </svg>
        <span>หากมีปัญหาสามารถติดต่อ Admin ได้ตลอด 24 ชั่วโมง</span>
      </div>
    </main>

    <!-- Bottom Menu -->
    <BottomMenu></BottomMenu>
  </div>
</template>

<script>
import firebase from "firebase/app";
import { buildWithdrawRequestFlex } from "@/services/withdrawRequestFlex";
import "firebase/database";
import moment from "moment-timezone";
import router from "./../router";
import liffService from "@/services/liff";
import TurnoverStatusCard from "@/components/withdrawal/TurnoverStatusCard.vue";
import {
  CustomerWithdrawalError,
  createWithdrawalRequestId,
  submitCustomerWithdrawal
} from "@/services/withdrawal";
const axios = require("axios").default;
export default {
  name: "Withdraw",
  components: {
    BottomMenu: require("./BottomMenu").default,
    TurnoverStatusCard
  },
  created() {
    // LIFF is already initialized by router guard
    this.loadUserData();
  },
  data() {
    return {
      countWithdraw: 0,
      menuVisible: false,
      pictureUrl: "",
      history: [],
      credit: "loading..",
      creditTotal: "loading..",
      name: "",
      bank: "",
      accountno: "",
      money: "",
      isDisabled: false,
      isSubmitted: false,
      isShow: true,
      error: 0,
      validationError: "โปรดตรวจสอบยอดจำนวนที่จะถอน",
      submitError: "",
      turnoverDetails: null,
      requestId: "",
      withdrawableAmount: 0,
      withdrawableLoaded: false,
      bankImage: "https://vectorified.com/images/bank-icon-png-1.png",
      code: "",
      userId: null
    };
  },
  computed: {
    creditTotalNumber() {
      const value = Number(this.creditTotal.toString().replace(/,/g, ""));
      return Number.isFinite(value) ? value : 0;
    },
    maxWithdrawAmount() {
      return this.withdrawableLoaded ? this.withdrawableAmount : this.creditTotalNumber;
    }
  },
  methods: {
    validateNumber(event) {
      return event.charCode >= 48 && event.charCode <= 57;
    },
    setAmount(amount) {
      if (amount <= this.maxWithdrawAmount) {
        this.money = amount;
        this.clearValidationError();
      }
    },
    clearValidationError() {
      this.error = 0;
      this.validationError = "โปรดตรวจสอบยอดจำนวนที่จะถอน";
    },
    async storeIP(key){
        await axios.get('https://api.ipify.org?format=json').then(x => {
          if(x.data.ip && key){
            firebase.database().ref(`/users/${key}`).update({
              ip:x.data.ip
            })
          }
        });
    },
    async confirm() {
      const money = Number(this.money.toString().replace(/,/g,''));
      if (!Number.isFinite(money) || money < 50 || money > this.maxWithdrawAmount || !this.userId) {
        this.validationError = money > this.maxWithdrawAmount
          ? `ยอดที่ถอนได้สูงสุด ${this.numberWithCommas(this.maxWithdrawAmount)} บาท`
          : "ยอดถอนขั้นต่ำ 50 บาท";
        this.$refs["money"].focus();
        this.error = 1;
        return;
      }

      const isLocalhost =
        window.location.hostname === "localhost" ||
        window.location.hostname === "127.0.0.1";
      const accessToken =
        liffService.getAccessToken() ||
        (isLocalhost ? "local-withdraw-preview-token" : "");
      if (!accessToken) {
        this.submitError = "Session LINE หมดอายุ กรุณาเปิดหน้าถอนใหม่อีกครั้ง";
        this.turnoverDetails = null;
        return;
      }

      this.isDisabled = true;
      this.clearValidationError();
      this.submitError = "";
      this.turnoverDetails = null;
      this.requestId = this.requestId || createWithdrawalRequestId();

      try {
        const result = await submitCustomerWithdrawal({
          accessToken,
          amount: money,
          requestId: this.requestId
        });
        this.withdrawableAmount = Number(result.withdrawableAmount) || this.withdrawableAmount;
        this.withdrawableLoaded = true;
        this.isSubmitted = true;

        try {
          if (liffService.isInClient) {
            await window.liff.sendMessages([
              buildWithdrawRequestFlex(result.withdrawAmount, moment().tz("Asia/Bangkok"))
            ]);
            window.liff.closeWindow();
          }
        } catch (messageError) {
          console.error("Withdrawal was created but LINE confirmation failed", messageError);
        }
      } catch (requestError) {
        const error = requestError instanceof CustomerWithdrawalError
          ? requestError
          : new CustomerWithdrawalError(
            "WITHDRAW_REQUEST_FAILED",
            "ระบบตรวจสอบสิทธิ์ถอนไม่สำเร็จ กรุณาลองใหม่อีกครั้ง"
          );
        this.submitError = error.message;
        this.turnoverDetails = error.code === "TURNOVER_NOT_MET" ? error.details : null;
        if (error.code === "CREDIT_NOT_ENOUGH" && error.details) {
          this.withdrawableAmount = Number(error.details.withdrawableAmount) || 0;
          this.withdrawableLoaded = true;
        }
      } finally {
        this.isDisabled = false;
      }
    },
    addCommasInNubmer: value => {
      let num, oneDigit, str;
      if (typeof value === "number") {
        num = value.toFixed(2);
      } else {
        num = value;
      }

      str = num.toString().split(".");
      if (str[0].length >= 4) {
        str[0] = str[0].replace(/(\d)(?=(\d{3})+$)/g, "$1,");
      }
      if (str[1]) {
        oneDigit = str[1].split("");
        if (str[1] === "00") {
          str = str[0];
        } else if (oneDigit[1] === "0") {
          str[1] = oneDigit[0];
          str = str.join(".");
        } else {
          str = str.join(".");
        }
      } else {
        str = str[0];
      }
      return str;
    },
    numberWithCommas(x) {
      return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    },
    async loadUserData() {
      const isLocalhost = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
      
      // Get profile from centralized service (already fetched by router guard)
      let profile = liffService.profile;
      
      // Mock LIFF profile for localhost development
      if (isLocalhost && !profile) {
        profile = {
          userId: "UmockUserIdForDev",
          displayName: "Dev Tester",
          pictureUrl: "",
          statusMessage: ""
        };
      }
      
      if (!profile) return;

      this.userId = profile.userId || "";
      this.pictureUrl = profile.pictureUrl || "none";
      
      if (this.userId) {
        // Development mode on localhost
        if (isLocalhost) {
          this.name = "ทดสอบ ระบบ";
          this.bank = "ธนาคารไทยพาณิชย์";
          this.accountno = "XXX-X-XXXXX-X";
          this.code = "DEV001";
          this.creditTotal = "10,000";
          this.credit = "10,000";
          this.withdrawableAmount = 10000;
          this.withdrawableLoaded = true;
          return;
        }
        
        const users = (
          await firebase
            .database()
            .ref(`/users`)
            .orderByChild("userId")
            .equalTo(this.userId)
            .once("value")
        ).val();
        if (!users) {
          router.replace("/register");
        } else {
          let user = {};
          let key = "";
          for (const x in users) {
            user = users[x];
            key = x;
          }
          await this.storeIP(key);
          this.name = user.firstname + " " + user.lastname;
          if (user.bank && user.account_no) {
            let bank = "";
            if (user.bank === "scb") bank = "ธนาคารไทยพาณิชย์";
            else if (user.bank === "bankokbank") bank = "ธนาคารกรุงเทพ";
            else if (user.bank === "kbank") bank = "ธนาคารกสิกรไทย";
            else if (user.bank === "ktb") bank = "ธนาคารกรุงไทย";
            else if (user.bank === "krungsri") bank = "ธนาคารกรุงศรีอยุธยา";
            else if (user.bank === "tmb") bank = "ธนาคารทหารไทย";
            else if (user.bank === "gsb") bank = "ธนาคารออมสิน";
            else if (user.bank === "lh") bank = "ธนาคารแลนด์ แอนด์ เฮ้าส์";
            else if (user.bank === "tisco") bank = "ธนาคารทิสโก้";
            else if (user.bank === "thanachart") bank = "ธนาคารธนชาต";
            else if (user.bank === "kiatnakin") bank = "ธนาคารเกียรตินาคิน";
            else if (user.bank === "uob") bank = "ธนาคารยูโอบี";
            else if (user.bank === "ธนาคารอิสลาม") bank = "ธนาคารอิสลาม";
            else if (user.bank === "ghbank") bank = "ธนาคาร อาคารสงเคราะห์";
            else if (user.bank === "ธนาคารนครหลวง") bank = "ธนาคาร นครหลวง";
            else if (user.bank === "cimb") bank = "ธนาคาร CIMB";
            else if (user.bank === "baac") bank = "ธนาคารเพื่อการเกษตรและสหกรณ์การเกษตร";
            else if (user.bank === "truewallet") bank = "TrueMoney Wallet";
            this.bank = bank;
            this.accountno = user.account_no;
            this.code = user.code;
          }
          axios({
            method: "post",
            url: "https://ufaeasy.apifast.xyz/getcredit",
            data: {
              id: this.userId
            }
          }).then(async response => {
            if (response.data && response.data.status) {
              this.credit = this.addCommasInNubmer(
                Number(response.data.credit) - Number(response.data.igbalance)
              );
              this.creditTotal = this.addCommasInNubmer(
                Number(response.data.credit) + Number(response.data.igbalance)
              );
              this.withdrawableAmount = Number(response.data.withdrawableAmount);
              this.withdrawableLoaded = Number.isFinite(this.withdrawableAmount);
            }
          });
        }
      }
    }
  }
};
</script>

<style lang="scss" scoped>
// ============================================================================
// N8N THEME - WITHDRAW PAGE
// ============================================================================

// Design Tokens
$n8n-bg: #0a0a0a;
$n8n-surface: #151515;
$n8n-surface-hover: #1f1f1f;
$n8n-border: rgba(255, 255, 255, 0.08);
$n8n-primary: #FF6B35;
$n8n-primary-soft: rgba(255, 107, 53, 0.15);
$n8n-danger: #ef4444;
$n8n-danger-soft: rgba(239, 68, 68, 0.15);
$n8n-success: #10b981;
$n8n-success-soft: rgba(16, 185, 129, 0.15);
$n8n-text: #ffffff;
$n8n-text-secondary: rgba(255, 255, 255, 0.6);
$n8n-text-muted: rgba(255, 255, 255, 0.4);
$n8n-radius: 12px;
$n8n-radius-sm: 8px;

.n8n-page {
  min-height: 100vh;
  background: transparent;
  color: $n8n-text;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  padding-bottom: 100px;
}

// Header
.n8n-header {
  position: sticky;
  top: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  padding-top: calc(1rem + env(safe-area-inset-top, 0px));
  background: rgba(10, 10, 10, 0.85);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-bottom: 1px solid $n8n-border;

  &__brand {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  &__logo {
    height: 44px;
    width: auto;
  }

  &__info {
    display: flex;
    flex-direction: column;
  }

  &__title {
    font-size: 1rem;
    font-weight: 600;
    color: $n8n-text;
  }

  &__badge {
    font-size: 0.75rem;
    color: $n8n-primary;
    font-weight: 500;
  }

  &__avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background-size: cover;
    background-position: center;
    background-color: $n8n-surface;
    border: 2px solid $n8n-border;
  }
}

// Main Content
.n8n-main {
  padding: 1.5rem 1rem;
  max-width: 480px;
  margin: 0 auto;
}

// Hero Section
.n8n-hero {
  text-align: center;
  margin-bottom: 1.5rem;
  
  &__icon {
    width: 64px;
    height: 64px;
    margin: 0 auto 1rem;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 16px;
    color: white;
    
    &--withdraw {
      background: linear-gradient(135deg, $n8n-danger 0%, #dc2626 100%);
      box-shadow: 0 8px 32px rgba(239, 68, 68, 0.3);
    }
  }

  &__title {
    font-size: 1.75rem;
    font-weight: 700;
    margin: 0 0 0.5rem;
    background: linear-gradient(135deg, #fff 0%, rgba(255,255,255,0.8) 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  &__subtitle {
    font-size: 0.95rem;
    color: $n8n-text-secondary;
    margin: 0;
  }
}

// Balance Card
.n8n-balance-card {
  background: linear-gradient(135deg, $n8n-primary 0%, #E55520 100%);
  border-radius: $n8n-radius;
  padding: 1.25rem;
  text-align: center;
  margin-bottom: 1.5rem;
  box-shadow: 0 8px 32px rgba(255, 107, 53, 0.25);

  &__label {
    font-size: 0.875rem;
    color: rgba(255, 255, 255, 0.8);
    margin-bottom: 0.5rem;
  }

  &__amount {
    display: flex;
    align-items: baseline;
    justify-content: center;
    gap: 0.5rem;
  }

  &__value {
    font-size: 2rem;
    font-weight: 700;
    color: white;
  }

  &__currency {
    font-size: 1rem;
    color: rgba(255, 255, 255, 0.8);
  }
}

// Cards
.n8n-card {
  background: $n8n-surface;
  border: 1px solid $n8n-border;
  border-radius: $n8n-radius;
  margin-bottom: 1rem;
  overflow: hidden;

  &__header {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 1rem 1.25rem;
    background: rgba(255, 255, 255, 0.02);
    border-bottom: 1px solid $n8n-border;
    font-weight: 600;
    color: $n8n-text;
    
    svg {
      color: $n8n-primary;
    }
  }

  &__badge {
    margin-left: auto;
    padding: 0.25rem 0.75rem;
    background: $n8n-success-soft;
    color: $n8n-success;
    border-radius: 50px;
    font-size: 0.75rem;
    font-weight: 600;
  }

  &__body {
    padding: 1.25rem;
  }
}

// Form Fields
.n8n-field {
  margin-bottom: 1.25rem;

  &__label {
    display: block;
    font-size: 0.875rem;
    font-weight: 500;
    color: $n8n-text-secondary;
    margin-bottom: 0.5rem;
  }

  &__helper {
    display: block;
    font-size: 0.75rem;
    color: $n8n-text-muted;
    margin-top: 0.375rem;
  }

  &__error {
    display: block;
    font-size: 0.75rem;
    color: $n8n-danger;
    margin-top: 0.375rem;
  }
}

.n8n-input {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem 1rem;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid $n8n-border;
  border-radius: $n8n-radius-sm;
  transition: all 0.2s ease;
  
  svg {
    color: $n8n-text-muted;
    flex-shrink: 0;
  }
  
  input {
    flex: 1;
    background: transparent;
    border: none;
    outline: none;
    font-size: 1rem;
    color: $n8n-text;
    
    &::placeholder {
      color: $n8n-text-muted;
    }
  }
  
  &:focus-within {
    border-color: $n8n-primary;
    background: rgba(255, 107, 53, 0.05);
  }

  &--disabled {
    background: rgba(255, 255, 255, 0.02);
    
    span {
      color: $n8n-text-secondary;
    }
  }

  &--error {
    border-color: $n8n-danger;
    background: $n8n-danger-soft;
  }
}

// Quick Amounts
.n8n-quick-amounts {
  &__label {
    display: block;
    font-size: 0.875rem;
    color: $n8n-text-muted;
    margin-bottom: 0.75rem;
    text-align: center;
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 0.5rem;
  }
}

.n8n-quick-btn {
  padding: 0.75rem;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid $n8n-border;
  border-radius: $n8n-radius-sm;
  color: $n8n-text;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:active {
    transform: scale(0.96);
    background: $n8n-primary-soft;
    border-color: $n8n-primary;
    color: $n8n-primary;
  }
  
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}

// Bank Info
.n8n-bank-info {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.02);
  border-radius: $n8n-radius-sm;
  border: 1px solid $n8n-border;

  &__icon {
    width: 56px;
    height: 56px;
    background: white;
    border-radius: $n8n-radius-sm;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    
    img {
      width: 40px;
      height: 40px;
      object-fit: contain;
    }
  }

  &__details {
    flex: 1;
    min-width: 0;
  }

  &__bank {
    font-size: 0.9rem;
    font-weight: 600;
    color: $n8n-primary;
    margin-bottom: 0.25rem;
  }

  &__number {
    font-size: 1.1rem;
    font-weight: 700;
    color: $n8n-text;
    font-family: 'SF Mono', 'Consolas', monospace;
    margin-bottom: 0.25rem;
  }

  &__name {
    font-size: 0.85rem;
    color: $n8n-text-secondary;
  }
}

// Submit
.n8n-submit {
  margin-top: 1.5rem;
}

.n8n-success-banner {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 1rem;
  padding: 1rem;
  border: 1px solid rgba(16, 185, 129, 0.38);
  border-radius: $n8n-radius;
  background: $n8n-success-soft;

  &__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    flex: 0 0 28px;
    border-radius: 50%;
    background: $n8n-success;
    color: #04130e;
    font-weight: 800;
  }

  strong,
  span {
    display: block;
  }

  strong {
    color: #a7f3d0;
    font-size: 0.9rem;
  }

  div > span {
    margin-top: 0.2rem;
    color: $n8n-text-secondary;
    font-size: 0.8rem;
  }
}

// Buttons
.n8n-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 1rem 1.5rem;
  border: none;
  border-radius: 50px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &--primary {
    background: linear-gradient(135deg, $n8n-danger 0%, #dc2626 100%);
    color: white;
    box-shadow: 0 4px 16px rgba(239, 68, 68, 0.3);
    
    &:active {
      transform: scale(0.98);
      box-shadow: 0 2px 8px rgba(239, 68, 68, 0.3);
    }
    
    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
      transform: none;
    }
  }
  
  &--full {
    width: 100%;
  }
}

// Spinner
.n8n-spinner {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: n8n-spin 0.8s linear infinite;
}

@keyframes n8n-spin {
  to { transform: rotate(360deg); }
}

// Info Footer
.n8n-info-footer {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 1rem;
  margin-top: 1rem;
  color: $n8n-text-muted;
  font-size: 0.85rem;
  text-align: center;
  
  svg {
    flex-shrink: 0;
  }
}
</style>
